import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import FormSelectBox from '@views/components/Inputs/FormSelectBox';
import { useGetQuoteProductClassesList } from '@views/screens/Quote/hooks';
import type { FormItemProps, SelectProps } from 'antd';
import i18n from 'i18next';
import { routeFileSelector } from '@utilz/i18n';

interface QuoteProductClassSelectProps extends Pick<FormItemProps, 'rules' | 'name'>, Pick<SelectProps, 'placeholder'> {
  label?: string;
  margin?: string;
  editMode?: boolean;
  onChange?: (value: unknown) => void;
  tooltip?: string;
  isDefault?: boolean;
  isRequired?: boolean;
  tabIndex?: number;
  dataTestId?: string;
}

export const QuoteProductClassSelect: FC<QuoteProductClassSelectProps> = ({
  placeholder = i18n.t('translation:default.fields.locationType.placeholder'),
  label = i18n.t('translation:default.fields.locationType.label'),
  rules = [],
  name,
  editMode,
  onChange,
  margin = 'mb-0',
  tooltip,
  isDefault = false,
  tabIndex,
  dataTestId,
  isRequired = false,
}) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const { data, isLoading } = useGetQuoteProductClassesList();

  const options = useMemo(() => {
    const nonHazardous = { label: 'Non Hazardous', value: 'N/A' };
    if (!data) {
      return [nonHazardous];
    }
    return [nonHazardous, ...data];
  }, [data]);

  return (
    <FormSelectBox
      loading={isLoading}
      options={isDefault ? [{ label: t('translation:default.buttons.select'), value: '' }, ...options] : options}
      placeholder={placeholder}
      label={label}
      margin={margin}
      isRequired={isRequired}
      rules={rules}
      name={name}
      editMode={editMode}
      tooltip={tooltip}
      {...(onChange ? { onChange } : {})}
      tabIndex={tabIndex}
      dataTestId={dataTestId}
    />
  );
};
